import { ChangeDetectionStrategy, Component, computed, inject, input, model, output, signal } from '@angular/core';
import { AbstractToggleFormComponent, EditablePanelComponent, LabelComponent } from '@spike/scaffolds';
import { AsyncApiService } from '@spike/core';
import { FormFactoryService } from '../../../shared/services/form-factory.service';
import { OrderGeneralFormComponent } from "../../forms/order-general-form/order-general-form";
import { OrderPositionsFormComponent } from "../../forms/order-positions-form/order-positions-form";
import { FormArray } from '@angular/forms';
import { ConfirmationDialogService } from '../../../shared/services/confirmation-dialog.service';
import { Order } from '../../models/order.model';
import { OrdineWorkflow } from 'src/app/features/workflow/models/workflow-status.enum';
import { EntityApiEnum } from 'src/app/config/entity-api.enum';
import { Position } from '../../models/position.model';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'spike-order-general-toggle-panel',
  providers:[],
  imports: [EditablePanelComponent, OrderGeneralFormComponent, OrderPositionsFormComponent, LabelComponent],
  templateUrl: './order-general-toggle-panel.html'
})
export class OrderGeneralTogglePanel extends AbstractToggleFormComponent<Order> {
  
  // Inject
  private readonly asyncApi = inject(AsyncApiService);
  private readonly formFactory = inject(FormFactoryService);
  private readonly confirmationDialogService = inject(ConfirmationDialogService);

  // Input
  readonly data = input.required<Order>();
  
  // Output
  public readonly onSave = output<Order|undefined>();
  public readonly onPositionSelect = output<number>();

  // State
  public readonly form = this.formFactory.createOrderForm();
  public readonly positionReady = signal<boolean>(false);
  public readonly ordineWorkflow=OrdineWorkflow;
  public override formView= model(true);

  public readonly isInsertForm = computed(()=> {
    const orderId = this.data()?.orderId;
    return !orderId
  })
  
  public override disabledFields = computed(()=>{
    const data = this.data();
    return this.ordineWorkflow.find(x=>x.id == data?.stateId)?.disabledFields??[];
  })

  public readonly isReadOnly= computed(()=>{
    const data = this.data();
    return !!this.ordineWorkflow.find(x=>x.id == data?.stateId)?.readonly;
  })

  async save() {
    this.form.markAllAsTouched();

    if (this.form.valid) {
      const generalValues = this.form.getRawValue();
      const toSaveData: any = {
        ...generalValues,
        orderId: this.data()?.orderId,
      };
      let order = undefined;
      if(this.data()?.orderId){
        let codeConflictPosition = await this.checkPositionCode(toSaveData.positions);
        if(codeConflictPosition){
          this.confirmationDialogService.show('La posizione con codice "' + codeConflictPosition + '" è già associata ad un altro ordine. Vuoi salvare comunque?', async () => {
            order = await this.asyncApi.updateById<Order>(EntityApiEnum.ORDER, toSaveData);
            this.onUpdate(order);
          });
        } else{
          order = await this.asyncApi.updateById<Order>(EntityApiEnum.ORDER, toSaveData);
          this.onUpdate(order);
        } 
      }else{
        order = await this.asyncApi.insert<Order>(EntityApiEnum.ORDER, toSaveData);
        this.onUpdate(order)
      }
    }
  }

  private async checkPositionCode(positions: Position[]): Promise<string|undefined>{
    for (const position of positions) {
      if(!position.positionId){
        let positionFinded: Position[]  = (await this.asyncApi.search<Position>(EntityApiEnum.POSITION, { code:  position.code},{ currentPage: 0, rows: 10})).list;
          if(positionFinded.length > 0 && positionFinded.filter(pos => pos.orderId != this.data()?.orderId).length > 0){
          return position.code;
        }
      }
    }
    return undefined;
  }

  public onUpdate(order?: Order){
    this.onSave.emit(order);
  }

  get positionArray(): FormArray {
    return this.form.get('positions') as FormArray;
  }

  protected override buildFormArrays(data: Order): void {
    this.positionReady.set(false);
    super.fillFormArray(this.positionArray, data.positions, (position)=>this.formFactory.createPositionForm(position));
    this.positionReady.set(true);
  }

  addPositionRow() {
    this.positionArray.push(this.formFactory.createPositionForm());
  }

  deletePositionRow(index: number) {
    this.positionArray.removeAt(index);
  }

  selectPosition(positionId: number){
    this.onPositionSelect.emit(positionId)
  }

}